import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, ChevronDown, X, Plus } from 'lucide-react';

const Autocomplete = ({
    options = [],
    value = '', // Can be string or array if multiple is true
    onChange,
    placeholder = 'Search...',
    label = '',
    required = false,
    error = '',
    name = '',
    className = '',
    multiple = false,
    maxSelection = 3,
    creatable = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // Memoize selectedItems to prevent unnecessary re-renders in useEffect
    const selectedItems = useMemo(() => {
        return multiple ? (Array.isArray(value) ? value : (value ? [value] : [])) : [];
    }, [value, multiple]);

    // Sync searchTerm with value for single-select mode
    useEffect(() => {
        if (!multiple) {
            setSearchTerm(value || '');
        }
    }, [value, multiple]);

    // Handle filtering
    useEffect(() => {
        let filtered;
        if (searchTerm) {
            filtered = options.filter(option =>
                (option || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) &&
                (!multiple || !selectedItems.includes(option))
            );
        } else {
            filtered = multiple ? options.filter(opt => !selectedItems.includes(opt)) : options;
        }

        // Only update if filtered list actually changed to avoid infinite loops
        setFilteredOptions(prev => {
            if (JSON.stringify(prev) === JSON.stringify(filtered)) return prev;
            return filtered;
        });
        setActiveIndex(-1);
    }, [searchTerm, options, selectedItems, multiple]);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                // Reset searchTerm on close if not multiple
                if (!multiple) setSearchTerm(value || '');
                else setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [wrapperRef, value, multiple]);

    const handleInputChange = (e) => {
        const newVal = e.target.value;
        setSearchTerm(newVal);
        if (!isOpen) setIsOpen(true);

        // For single select, if cleared, notify parent immediately
        if (!multiple && newVal === '') {
            onChange({ target: { name, value: '' } });
        }
    };

    const handleSelect = (option) => {
        if (multiple) {
            if (selectedItems.length < maxSelection && !selectedItems.includes(option)) {
                const newValue = [...selectedItems, option];
                onChange({ target: { name, value: newValue } });
                setSearchTerm('');
            }
        } else {
            setSearchTerm(option);
            onChange({ target: { name, value: option } });
        }
        setIsOpen(false);
    };

    const removeItem = (itemToRemove) => {
        const newValue = selectedItems.filter(item => item !== itemToRemove);
        onChange({ target: { name, value: newValue } });
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') setIsOpen(true);
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                const limit = creatable && searchTerm && !filteredOptions.includes(searchTerm)
                    ? filteredOptions.length
                    : filteredOptions.length - 1;
                setActiveIndex(prev => (prev < limit ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prev => (prev > 0 ? prev - 1 : prev));
                break;
            case 'Enter':
                e.preventDefault();
                if (creatable && activeIndex === filteredOptions.length && searchTerm) {
                    handleSelect(searchTerm);
                } else if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[activeIndex]);
                } else if (filteredOptions.length > 0 && searchTerm) {
                    const exactMatch = filteredOptions.find(opt => opt.toLowerCase() === searchTerm.toLowerCase());
                    if (exactMatch) handleSelect(exactMatch);
                    else if (filteredOptions.length === 1) handleSelect(filteredOptions[0]);
                    else if (creatable) handleSelect(searchTerm);
                } else if (creatable && searchTerm) {
                    handleSelect(searchTerm);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                if (!multiple) setSearchTerm(value || '');
                else setSearchTerm('');
                break;
            case 'Backspace':
                if (multiple && !searchTerm && selectedItems.length > 0) {
                    removeItem(selectedItems[selectedItems.length - 1]);
                }
                break;
            default:
                break;
        }
    };

    return (
        <div className={`autocomplete-wrapper ${className}`} ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            {label && (
                <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '700',
                    color: 'var(--proto-white)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                }}>
                    {label} {required && <span style={{ color: 'var(--red)' }}>*</span>}
                    {multiple && <span style={{ fontWeight: '400', color: 'var(--text-faint)', marginLeft: '8px', textTransform: 'none' }}>(Max {maxSelection})</span>}
                </label>
            )}

            <div className={`input-container ${isOpen ? 'is-open' : ''}`} style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                background: 'var(--input-bg)',
                border: error ? '1px solid var(--red)' : (isOpen ? '1px solid var(--cyan)' : '1px solid var(--border)'),
                borderRadius: '12px',
                padding: multiple && selectedItems.length > 0 ? '6px 14px' : '0 14px',
                minHeight: '48px',
                boxShadow: isOpen ? 'var(--input-focus-shadow)' : 'none',
                transition: 'all 0.2s ease',
                cursor: 'text',
                gap: '8px'
            }} onClick={() => inputRef.current?.focus()}>
                {!multiple && <Search size={18} color="var(--text-faint)" style={{ marginRight: '6px', flexShrink: 0 }} />}

                {multiple && selectedItems.map((item, idx) => (
                    <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: 'var(--cyan-dim)',
                        color: 'var(--cyan)',
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        border: '1px solid var(--cyan-dim)'
                    }}>
                        {item}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeItem(item); }}
                            style={{ background: 'none', border: 'none', marginLeft: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                            <X size={12} color="var(--cyan)" />
                        </button>
                    </div>
                ))}

                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={multiple && selectedItems.length > 0 ? "" : placeholder}
                    disabled={multiple && selectedItems.length >= maxSelection}
                    style={{
                        border: 'none',
                        outline: 'none',
                        flex: 1,
                        minWidth: '120px',
                        height: '42px',
                        fontSize: '15px',
                        color: 'var(--proto-white)',
                        background: 'transparent',
                        fontFamily: 'inherit'
                    }}
                />

                {!multiple && searchTerm && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSearchTerm(''); onChange({ target: { name, value: '' } }); }}
                        style={{ background: 'none', border: 'none', padding: '4px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    >
                        <X size={14} color="var(--text-faint)" />
                    </button>
                )}

                <ChevronDown
                    size={18}
                    color="var(--text-faint)"
                    style={{
                        transition: 'transform 0.2s ease',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        cursor: 'pointer'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(!isOpen);
                    }}
                />
            </div>

            {isOpen && (
                <div className="options-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '6px',
                    background: 'var(--graphite)',
                    border: '1px solid var(--border)',
                    borderRadius: '12px',
                    boxShadow: 'var(--shadow-lg)',
                    zIndex: 1000,
                    maxHeight: '260px',
                    overflowY: 'auto',
                    animation: 'dropdownFadeIn 0.2s ease-out'
                }}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <div
                                key={index}
                                className={`option-item ${index === activeIndex ? 'active' : ''}`}
                                onClick={() => handleSelect(option)}
                                onMouseEnter={() => setActiveIndex(index)}
                                style={{
                                    padding: '12px 18px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: index === activeIndex ? 'var(--cyan)' : 'var(--proto-white)',
                                    background: index === activeIndex ? 'var(--cyan-dim)' : 'transparent',
                                    borderBottom: '1px solid var(--border)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontWeight: index === activeIndex ? '700' : '400',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                {option}
                            </div>
                        ))
                    ) : null}

                    {creatable && searchTerm && !options.some(opt => (opt || '').toString().toLowerCase() === searchTerm.toLowerCase()) && (
                        <div
                            className={`option-item ${activeIndex === filteredOptions.length ? 'active' : ''}`}
                            onClick={() => handleSelect(searchTerm)}
                            onMouseEnter={() => setActiveIndex(filteredOptions.length)}
                            style={{
                                padding: '12px 18px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: activeIndex === filteredOptions.length ? 'var(--cyan)' : 'var(--proto-white)',
                                background: activeIndex === filteredOptions.length ? 'var(--cyan-dim)' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontWeight: activeIndex === filteredOptions.length ? '700' : '400',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            <Plus size={14} />
                            <span>Create "{searchTerm}"</span>
                        </div>
                    )}

                    {filteredOptions.length === 0 && (!creatable || !searchTerm) && (
                        <div style={{ padding: '16px', color: 'var(--text-faint)', fontSize: '14px', textAlign: 'center' }}>
                            {searchTerm ? "No results found" : (multiple && selectedItems.length >= maxSelection ? "Max selection reached" : "Start typing to search")}
                        </div>
                    )}
                </div>
            )}

            {error && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{error}</span>}

            <style>{`
        @keyframes dropdownFadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .options-dropdown::-webkit-scrollbar {
          width: 6px;
        }
        .options-dropdown::-webkit-scrollbar-track {
          background: transparent;
        }
        .options-dropdown::-webkit-scrollbar-thumb {
          background: var(--border);
          border-radius: 10px;
        }
        .options-dropdown::-webkit-scrollbar-thumb:hover {
          background: var(--border);
        }
      `}</style>
        </div>
    );
};

export default Autocomplete;


